import { useCallback } from 'react';
import {
  useCreateAnnotationMutation,
  useUpdateAnnotationMutation,
  useDeleteAnnotationMutation,
} from './useAnnotationHooks';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import { useAnnotationStore } from './annotationStore';
import { Note } from './types';

/**
 * Custom hook that provides a complete CRUD interface for working with annotations
 * Directly uses React Query mutations without duplicating state in Zustand
 */
export const useAnnotationActions = () => {
  // Get the currently selected object ID
  const selectedObject = useStore(useUnityBuildStore).selectedObject;
  const objectId = selectedObject?.id || '';

  // Get dimensional mode from annotation store
  const isDimensionalMode = useStore(useAnnotationStore).isDimensionalMode;

  // Get mutations
  const createMutation = useCreateAnnotationMutation();
  const updateMutation = useUpdateAnnotationMutation();
  const deleteMutation = useDeleteAnnotationMutation();

  /**
   * Creates a new annotation
   */
  const createAnnotation = useCallback(
    (x: number, y: number, text: string = '') => {
      if (!objectId) {
        console.error('Cannot create annotation: No object selected');
        return null;
      }

      // Return a temporary ID for immediate UI updates
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Create the annotation data structure expected by the mutation
      const annotationData = {
        objectid: objectId,
        tempId: tempId, // Pass the temp ID to the mutation
        annotationdata: {
          x: x.toString(),
          y: y.toString(),
          text: text || '',
          type: 0, // AnnotationType.TEXT
          isdimensional: isDimensionalMode,
        },
      };

      // Persist to backend - this will automatically update the query cache
      createMutation.mutate(annotationData);

      return tempId;
    },
    [createMutation, objectId, isDimensionalMode]
  );

  /**
   * Updates an existing annotation
   */
  const updateAnnotation = useCallback(
    (id: string, updates: Partial<Note>) => {
      if (!objectId) {
        console.error('Cannot update annotation: No object selected');
        return;
      }

      // Persist to backend - this will automatically update the query cache
      updateMutation.mutate({
        note: {
          id,
          ...updates,
        },
        objectId,
      });
    },
    [updateMutation, objectId]
  );

  /**
   * Deletes an annotation
   */
  const deleteAnnotation = useCallback(
    (id: string) => {
      if (!objectId) {
        console.error('Cannot delete annotation: No object selected');
        return;
      }

      // Persist to backend - this will automatically update the query cache
      deleteMutation.mutate({
        id,
        objectId,
      });
    },
    [deleteMutation, objectId]
  );

  return {
    createAnnotation,
    updateAnnotation,
    deleteAnnotation,
    isCreating: createMutation.isLoading,
    isUpdating: updateMutation.isLoading,
    isDeleting: deleteMutation.isLoading,
    hasError: createMutation.isError || updateMutation.isError || deleteMutation.isError,
  };
};
